import { 
  BookOpen, 
  TrendingUp, 
  ShieldCheck, 
  Cpu, 
  Zap, 
  Brain, 
  Layers,
  BarChart3,
  Activity,
  Target,
  Clock,
  Award
} from 'lucide-react';

export interface Lesson {
  id: string;
  title: string;
  duration: string;
  type: 'video' | 'interactive' | 'quiz' | 'reading';
  completed: boolean;
}

export interface Course {
  id: string;
  title: string;
  description: string;
  lessons: Lesson[];
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  category: string;
  icon: any;
}

export interface LearningTrack {
  id: string;
  title: string;
  description: string;
  courses: Course[];
  progress: number;
  icon: any;
}

export const ACADEMY_DATA: LearningTrack[] = [
  {
    id: 'beginner',
    title: 'Beginner Trading',
    description: 'Master the foundations of financial markets and trading mechanics.',
    progress: 45,
    icon: BookOpen,
    courses: [
      {
        id: 'order-types',
        title: 'Understanding Order Types',
        description: 'Market, Limit, Stop, and Iceberg orders explained.',
        level: 'Beginner',
        category: 'Foundations',
        icon: Layers,
        lessons: [
          { id: 'mkt-vs-limit', title: 'Market vs Limit Orders', duration: '5m', type: 'reading', completed: true },
          { id: 'stop-orders', title: 'Stop Loss Mechanics', duration: '8m', type: 'interactive', completed: true },
          { id: 'iceberg', title: 'Institutional Iceberg Orders', duration: '12m', type: 'video', completed: false },
        ]
      },
      {
        id: 'tech-analysis',
        title: 'Technical Analysis Foundations',
        description: 'Reading price action, support/resistance, and volume.',
        level: 'Beginner',
        category: 'Analysis',
        icon: TrendingUp,
        lessons: [
          { id: 'candlesticks', title: 'Candlestick Patterns', duration: '15m', type: 'interactive', completed: false },
          { id: 'sup-res', title: 'Support & Resistance', duration: '10m', type: 'reading', completed: false },
        ]
      }
    ]
  },
  {
    id: 'institutional',
    title: 'Institutional Market Structure',
    description: 'Learn how banks and hedge funds move the markets.',
    progress: 12,
    icon: Target,
    courses: [
      {
        id: 'liquidity',
        title: 'Liquidity & Market Makers',
        description: 'The role of DMMs and liquidity providers.',
        level: 'Advanced',
        category: 'Structure',
        icon: Activity,
        lessons: [
          { id: 'bid-ask', title: 'Bid-Ask Spread Dynamics', duration: '10m', type: 'video', completed: true },
          { id: 'order-flow', title: 'Order Flow Imbalance', duration: '20m', type: 'interactive', completed: false },
        ]
      },
      {
        id: 'level-2',
        title: 'Reading Level 2 Data',
        description: 'Analyzing the depth of book and dark pool activity.',
        level: 'Advanced',
        category: 'Structure',
        icon: BarChart3,
        lessons: [
          { id: 'tape-reading', title: 'Time & Sales Analysis', duration: '25m', type: 'interactive', completed: false },
        ]
      }
    ]
  },
  {
    id: 'options',
    title: 'Options Trading',
    description: 'From basic calls/puts to complex multi-leg strategies.',
    progress: 0,
    icon: Zap,
    courses: [
      {
        id: 'greeks',
        title: 'Options Greeks',
        description: 'Delta, Gamma, Theta, and Vega risk management.',
        level: 'Intermediate',
        category: 'Options',
        icon: Brain,
        lessons: [
          { id: 'delta', title: 'Delta & Probability', duration: '15m', type: 'video', completed: false },
          { id: 'theta', title: 'Time Decay (Theta)', duration: '12m', type: 'reading', completed: false },
        ]
      }
    ]
  },
  {
    id: 'risk',
    title: 'Risk Management',
    description: 'Protecting capital and managing drawdown.',
    progress: 30,
    icon: ShieldCheck,
    courses: [
      {
        id: 'frameworks',
        title: 'Risk Management Frameworks',
        description: 'Position sizing, R-multiple, and Kelly Criterion.',
        level: 'Intermediate',
        category: 'Risk',
        icon: ShieldCheck,
        lessons: [
          { id: 'pos-sizing', title: 'Optimal Position Sizing', duration: '10m', type: 'interactive', completed: true },
        ]
      }
    ]
  },
  {
    id: 'algo',
    title: 'Algorithmic Trading',
    description: 'Automating strategies and backtesting models.',
    progress: 5,
    icon: Cpu,
    courses: [
      {
        id: 'backtesting',
        title: 'Backtesting Foundations',
        description: 'Avoiding curve fitting and survivorship bias.',
        level: 'Advanced',
        category: 'Algo',
        icon: Clock,
        lessons: [
          { id: 'bias', title: 'Common Backtesting Biases', duration: '15m', type: 'reading', completed: false },
        ]
      }
    ]
  }
];

export const STRATEGY_TUTORIALS = [
  { id: 'scalping', title: 'Scalping Strategies', description: 'Profiting from micro-moves in high liquidity.', icon: Zap },
  { id: 'momentum', title: 'Momentum Trading', description: 'Riding strong trends with high volume.', icon: TrendingUp },
  { id: 'arbitrage', title: 'Arbitrage', description: 'Exploiting price discrepancies across markets.', icon: Layers },
  { id: 'mean-reversion', title: 'Mean Reversion', description: 'Trading the return to statistical averages.', icon: Activity },
];
